import { logRed } from 'utils/debugger';

import { ROBOT_MODEL } from 'types/models';
import {
  AllRobotTemplates,
  RobotTemplateAxisInfoDTO,
  SingleRobotTemplateDTO,
  SingleRobotTemplateDetailed,
  SingleRobotTemplateDetailedDTO,
} from 'types/models/RobotTemplate';

import { axiosRequest } from '../axios';

const ROBOT_MODEL_BY_ENUM_VALUE: Record<number, ROBOT_MODEL> = {
  1: ROBOT_MODEL.SPOT,
  2: ROBOT_MODEL.DRONE,
  3: ROBOT_MODEL.MAGNI,
  4: ROBOT_MODEL.ANYMAL,
  5: ROBOT_MODEL.TURTLEBOT,
  6: ROBOT_MODEL.GENERIC_QUADROCOPTER,
  7: ROBOT_MODEL.GENERIC_ROVER,
  8: ROBOT_MODEL.RB_WATCHER,
  9001: ROBOT_MODEL.SMARTPHONE,
};

export function getRobotModelByEnumValue(enumValue: number): ROBOT_MODEL | undefined {
  return ROBOT_MODEL_BY_ENUM_VALUE[enumValue];
}

const ROBOT_MODEL_IMAGE_MAP = {
  [ROBOT_MODEL.SPOT]: '/assets/robots/spot-600x341.png',
  [ROBOT_MODEL.MAGNI]: '/assets/robots/magni-800x653.png',
  [ROBOT_MODEL.TURTLEBOT]: '/assets/robots/turtlebot3-burger-389x441.png',
  [ROBOT_MODEL.DRONE]: '/assets/robots/dji-matrice-4td.png',
  [ROBOT_MODEL.ANYMAL]: '/assets/robots/anymal-1920x1920.webp',
  [ROBOT_MODEL.GENERIC_QUADROCOPTER]: '/assets/robots/generic_quadcopter.png',
  [ROBOT_MODEL.GENERIC_ROVER]: '/assets/robots/generic_rover.png',
  [ROBOT_MODEL.RB_WATCHER]: '/assets/robots/rb-watcher.png',
  [ROBOT_MODEL.SMARTPHONE]: '/assets/robots/body-camera.png',
} as const;

export function robotImageByModelName(modelName: ROBOT_MODEL): string {
  const isValidModelName = Object.values(ROBOT_MODEL).includes(modelName);
  if (!isValidModelName) {
    console.error('Invalid robot model name', modelName);
    throw new Error(`Invalid robot model name: ${modelName}`);
  }

  return ROBOT_MODEL_IMAGE_MAP[modelName];
}

function extendAllRobotTemplates(robot: SingleRobotTemplateDTO): AllRobotTemplates {
  const modelName = robot.modelName as ROBOT_MODEL;
  const imageUrl = robotImageByModelName(modelName);

  return {
    ...robot,
    modelName,
    imageUrl,
  };
}

function extendRobotTemplate(robot: SingleRobotTemplateDetailedDTO): SingleRobotTemplateDetailed {
  const modelName = robot.modelName as ROBOT_MODEL;
  const imageUrl = robotImageByModelName(modelName);

  return {
    ...robot,
    imageUrl,
    modelName,
  };
}

export const RobotTemplateUrls = {
  GetAllRobotTemplates: {
    method: 'get',
    url: `robotTemplate/GetAllRobotTemplates`,
  },
  GetRobotTemplate: {
    method: 'get',
    url: (robotTemplateId: number) => `robotTemplate/GetRobotTemplate/${robotTemplateId}`,
  },
  GetAxisInfoByMyRobotId: {
    method: 'get',
    url: (robotId: number) => `robotTemplate/GetAxisInfoByMyRobotId/${robotId}`,
  },
};

export class RobotTemplateManager {
  static async GetAllRobotTemplates(): Promise<AllRobotTemplates[]> {
    const { method, url } = RobotTemplateUrls.GetAllRobotTemplates;
    const { data } = await axiosRequest<SingleRobotTemplateDTO[]>(method, url);
    return data
      .filter((t) => t.modelName !== ROBOT_MODEL.TURTLEBOT)
      .map(extendAllRobotTemplates);
  }

  static async GetRobotTemplate(robotTemplateId: number): Promise<SingleRobotTemplateDetailed> {
    const { method, url } = RobotTemplateUrls.GetRobotTemplate;
    const { data } = await axiosRequest<SingleRobotTemplateDetailedDTO>(
      method,
      url(robotTemplateId),
    );
    return extendRobotTemplate(data);
  }

  static async GetAxisInfoByMyRobotId(
    myRobotId: number,
  ): Promise<RobotTemplateAxisInfoDTO> {
    const { method, url } = RobotTemplateUrls.GetAxisInfoByMyRobotId;
    const { data } = await axiosRequest<RobotTemplateAxisInfoDTO>(method, url(myRobotId));
    return data;
  }
}
